import { z } from 'zod';
import { getEnvVar } from '../../utils/env';
import { deviceInfoFromUserAgent } from './utils';

const StreamManifest = z.object({
  headers: z.union([z.array(z.any()), z.record(z.string(), z.any())]),
  uri: z.string(),
});

const DRM = z.object({
  uri: z.string(),
  headers: z.object({}),
  certificateUrl: z.string().optional(),
});

const StreamInfo = z.object({
  id: z.string(),
  bitrate: z.number(),
  mediaFormat: z.string(),
  mimeType: z.string(),
  manifest: StreamManifest,
  drmProxyType: z.string().optional(),
  drm: DRM.optional(),
});

const SubtitleInfo = z.object({
  name: z.string(),
  locale: z.string(),
  audience: z.string(),
  contentType: z.string(),
  url: z.string(),
});

const Client = z.object({
  buildName: z.string(),
  buildVersion: z.string(),
  countryCode: z.string(),
  deviceId: z.string(),
  deviceOS: z.string(),
  deviceType: z.string(),
  drm: z.string(),
  envPlatform: z.string(),
  envVersion: z.string(),
  pageUrl: z.string(),
  platform: z.string(),
  playerEvent: z.string(),
  playerState: z.string(),
  streamUrl: z.string(),
  userAgent: z.string(),
  videoFormat: z.string(),
  videoProtocol: z.string(),
  viewingSession: z.string(),
});

const Vod = z.object({
  duration: z.number(),
  position: z.string(),
});

const Live = z.object({
  onLiveEdge: z.string(),
  liveResumePossible: z.string(),
  position: z.string(),
});

const Progress = z.object({
  assetId: z.string(),
  categoryId: z.string(),
  title: z.string(),
  assetType: z.string(),
  playbackType: z.string(),
  eventNumber: z.number(),
  vod: Vod.optional(),
  live: Live.optional(),
});

const Marker = z.object({
  title: z.string().optional(),
  type: z.string(),
  startTime: z.number(),
  endTime: z.number(),
  images: z
    .object({
      defaultUrl: z.string(),
    })
    .optional(),
});

const Body = z.object({
  eventName: z.string(),
  originator: z.string(),
  originatorId: z.string(),
  subProfileId: z.string(),
  versions: z.array(z.string()),
  tenant: z.string(),
  logData: z.string(),
  client: Client,
  progress: Progress,
  timestamp: z.string(),
  eventInterval: z.number(),
});

const PlayerEventRequest = z.object({
  body: Body,
  eventInterval: z.number(),
  uri: z.string(),
});

export type PlayerEventRequestType = z.infer<typeof PlayerEventRequest>;

const Playback = z.object({
  assetId: z.string(),
  assetTitle: z.string(),
  categoryId: z.string(),
  startTime: z.number(),
  endTime: z.number(),
  duration: z.number(),
  transmissionTime: z.string(),
  isDrmProtected: z.boolean(),
  isLive: z.boolean(),
  recommendedStream: StreamInfo.optional(),
  alternativeStreams: z.array(StreamInfo).optional(),
  subtitles: z.array(SubtitleInfo).optional(),
  playerEventRequest: PlayerEventRequest.optional(),
  markers: z.array(Marker).optional(),
});

export type PlaybackType = z.infer<typeof Playback>;

const ErrorInfo = z.object({
  title: z.string().optional(),
  detail: z.string().optional(),
  code: z.string().optional(),
});

export type PlaybackErrorType = z.infer<typeof ErrorInfo>;

const responseValidator = z.object({
  data: z.array(Playback).optional(),
  errors: z.array(ErrorInfo).optional(),
});

export type PlaybackResponseType = z.infer<typeof responseValidator>;

export const getPlayback = (
  assetId: string,
  userAgent: string,
  jwt?: string,
  subprofileToken?: string,
  xff?: string,
) => {
  const authentication = jwt && { authorization: `Bearer ${jwt}` };
  const xVimondSubprofile = subprofileToken && {
    'X-Vimond-Subprofile': subprofileToken,
  };

  const newXff = xff ? xff.split(',')[0] : '';

  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    'user-agent': userAgent,
    ...(xff && { 'x-forwarded-for': newXff }),
    ...authentication,
    ...xVimondSubprofile,
  };

  // get device information based on user agent
  const body = jwt ? { device: deviceInfoFromUserAgent(userAgent) } : {};

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    redirect: 'follow',
    body: JSON.stringify(body),
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.play.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v2/asset/${assetId}/play?extraFields=markers,alternativeStreams,streamsTemplate`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      return responseValidator.parse(json);
    });
};
